import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ErrorOutline } from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography, styled,
} from '@mui/material';
import React from 'react';
import { FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import HeaderContainer from '../App/Components/Header/styles';
import PageHeader from '../App/Layouts/PageHeader';
import PageLayout from '../App/Layouts/PageLayout';
import { sanguine, ultraMarine } from '../utils/colors';
import Logo from './Headers/logo.png';

const CrashPageWrapper = styled(Box)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const CrashHeaderLogo = styled('img')({
  height: 18,
});

// PageLayout uses height:100% which overflows in a flex column; extend it to flex-grow instead.
const CrashLayout = styled(PageLayout)({
  flexGrow: 1,
  height: 'unset',
});

const CrashContentLayout = styled(Box)({
  flexGrow: 1,
  margin: '8px 12px',
  padding: '8px 12px',
  borderRadius: 6,
  backgroundColor: 'white',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'white',
    borderRadius: 6,
  },
});

const CrashErrorContent = styled(Box)({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  '& > .MuiSvgIcon-root': {
    fontSize: 58,
    color: sanguine[400],
  },
  '& > .MuiTypography-root': {
    color: sanguine[400],
    fontSize: 14,
    lineHeight: '16px',
    textAlign: 'center',
    fontWeight: 600,
  },
  '& > .MuiAccordion-root': {
    width: '100%',
    backgroundColor: 'transparent',
    boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.12)',
    '&::before': { display: 'none' },
    '& .MuiAccordionSummary-root .MuiTypography-root': {
      color: sanguine[400],
      fontSize: 14,
      lineHeight: '16px',
      fontWeight: 600,
    },
    '& .MuiAccordionDetails-root .MuiTypography-root': {
      fontFamily: 'monospace',
      fontSize: 11,
      fontWeight: 400,
      textAlign: 'left',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
      lineHeight: 1.5,
      color: sanguine[400],
    },
  },
  '& > .crash-buttons .MuiButton-root': {
    fontSize: 14,
    fontWeight: 600,
    color: ultraMarine[600],
    textTransform: 'none',
    display: 'flex',
    gap: 4,
    '&:hover': {
      textDecoration: 'underline',
    },
    '& svg': {
      fontSize: 16,
    },
  },
});

const CrashButtonRow = styled(Box)({
  display: 'flex',
  gap: 8,
});

const APP_CRASH_RELOAD_DELAY_S = 60;

export default function AppCrashFallback({ error }: FallbackProps) {
  const { t } = useTranslation();
  const [secondsLeft, setSecondsLeft] = React.useState(APP_CRASH_RELOAD_DELAY_S);

  React.useEffect(() => {
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          window.location.reload();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <CrashPageWrapper>
      <HeaderContainer>
        <CrashHeaderLogo src={Logo} alt="CogNeed" />
      </HeaderContainer>
      <CrashLayout>
        <PageHeader title={t('error.title')} />
        <CrashContentLayout>
          <CrashErrorContent>
            <ErrorOutline />
            <Typography>{t('error.text')}</Typography>
            <Accordion disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{error.message}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography component="pre">{error.stack}</Typography>
              </AccordionDetails>
            </Accordion>
            <CrashButtonRow className="crash-buttons">
              <Button onClick={() => window.location.reload()}>
                <RefreshIcon />
                {`${t('error.reload-page')} (${secondsLeft}s)`}
              </Button>
              <Button onClick={() => window.location.replace(window.location.href.split('#')[0])}>
                <RefreshIcon />
                {t('error.reload-from-home')}
              </Button>
            </CrashButtonRow>
          </CrashErrorContent>
        </CrashContentLayout>
      </CrashLayout>
    </CrashPageWrapper>
  );
}
